import { FhirService } from './core/service/fhir.service';
import { ToastrService } from 'ngx-toastr';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { LanguageService } from './core/service/language.service';
import { Component, Output, EventEmitter, OnDestroy, ChangeDetectorRef, AfterViewChecked, inject, OnInit } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { AuthenticationService } from './core/authentication/authentication.service';
import { ResizedEvent } from './shared/resize-event/resized-event';
import { IdleService } from './core/service/idle.service';
import { IdleEvent } from './core/model/idle-event';
import { MessengerService } from './core/service/messenger.service';
import { TranslateService } from '@ngx-translate/core';
import { UserNotification } from './user/model/user-notification';
import { Intercom } from 'ng-intercom-x';
import { VersionCheckService } from './core/service/version-check.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy, AfterViewChecked, OnInit
{
  /**
   * Event Emitter to trigger when content is resized. This is to control the left-nav rendering.
   */
  @Output() public contentResizeEvent: EventEmitter<ResizedEvent> = new EventEmitter<ResizedEvent>();

  /**
   * Authentication status
   */
  public isLoggedIn: Observable<boolean>;

  /**
   * Indicates if the left-nav menu is collaped. This is to adjust the view accordingly.
   */
  public collapedSideBar: boolean;

  /**
   * Indicates Idle countdown progress.
   */
  public idleCountdown: number;

  /**
   * Indicates Idle progress percentage used for controlling the progress bar.
   */
  public progressPercent: number;

  /**
   * Indicates if the unsaved dialog is open.
   */
  public unsavedDialogOpened = false;

  /**
   * Indicates if the Idle dialog is open.
   */
  public idleDialogOpened = false;

  /**
   * Observable to provide user confirmation before navigating and losing pending changes.
   */
  public navigateAwaySelection: Subject<boolean> = new Subject<boolean>();

  /**
   * Event handler to get notifications from Idle Service.
   */
  private idleServiceTimeoutSubscription: Subscription;

  /**
   * Event handler when authentication state has been changed.
   */
  private authServiceSubscription: Subscription;

  /**
   * Messaging subscription
   */
  private messagingSubscription: Subscription;

  /**
   * Version check service
   */
  private versionCheckService = inject(VersionCheckService);

  constructor(
    private authService: AuthenticationService,
    private authenticationService: AuthenticationService,
    private fhirService: FhirService,
    private idleService: IdleService,
    private translateService: TranslateService,
    private messengerService: MessengerService,
    private messageHandlerService: MessageHandlerService,
    private toastr: ToastrService,
    private changeDetectorRef: ChangeDetectorRef,
    private intercom: Intercom,
    private languageService: LanguageService)
  {
    this.isLoggedIn = this.authenticationService.isLoggedIn();
    this.languageService.switchLanguage(this.languageService.currentLanguage);

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'wsUserNoticedReceived' && this.authenticationService.isAuthenticated)
        {
          const userNotification: UserNotification = JSON.parse(message.data.body);
          this.translateService.get(['NOTIFICATION-FROM']).subscribe({
            next: (res) =>
            {
              // Override toast options
              const logoutDelay = userNotification.logoutDelay ? userNotification.logoutDelay * 1000 : 0;
              const toastrOptions = {
                disableTimeOut: !userNotification.forceLogout,
                closeButton: !userNotification.forceLogout,
                tapToDismiss: !userNotification.forceLogout,
                progressBar: userNotification.forceLogout,
                timeOut: logoutDelay,
                extendedTimeOut: logoutDelay
              };

              // Show notification to the user
              const notificationToast = this.messageHandlerService.displayDirectMessage(userNotification.type,
                userNotification.notification, res['NOTIFICATION-FROM'].replace('{{notificationFrom}}',
                  userNotification.fromName), toastrOptions);

              // Force logout?
              if (userNotification.forceLogout)
              {
                // When toast will hide, it is time to logout.
                notificationToast.onHidden.subscribe(() =>
                {
                  this.toastr.clear();
                  this.authService.logout();
                });
              }
            }
          });
        }
      }
    });

    this.idleServiceTimeoutSubscription = this.idleService.timeoutShowModal.subscribe({
      next: (event: IdleEvent) =>
      {
        this.idleDialogOpened = event.show;
        this.idleCountdown = event.countdown;
        if (event.show)
        {
          this.progressPercent = (event.countdown > 1) ? 100 - ((event.countdown / event.maxTimeout) * 100) : 100;
        }
        // Must force close if unsaved dialog is open and idle timed out
        else if (event.hasTimedOut)
        {
          this.collapedSideBar = false;
          if (this.unsavedDialogOpened)
          {
            this.continueUnsaved(true);
          }
        }
        this.changeDetectorRef.detectChanges();
      }
    });

    // On authenticate changes
    this.authServiceSubscription = this.authService.authenticationChange.subscribe({
      next: () =>
      {
        if (this.authenticationService && this.authenticationService.currentUser?.chatbot) 
        {
          this.intercom.boot({
            api_base: this.authenticationService.currentUser?.chatbot.baseUrl,
            app_id: this.authenticationService.currentUser?.chatbot.appId,
            email: this.authService.currentUser.email,
            name: this.authService.currentUser.name,
            phone: this.authService.currentUser.mobile,
            user_hash: this.authenticationService.currentUser?.chatbot.hash
          });
        }
        else
        {
          this.intercom.shutdown();
        }
      }
    });

    if (!this.isLoggedIn)
    {
      this.collapedSideBar = true;
    }
  }

  public get fhirEmbedded(): boolean
  {
    return this.fhirService.embedded;
  }

  public get fhirFullSite(): boolean
  {
    return this.fhirService.fullSite;
  }

  public ngOnInit(): void
  {
    this.versionCheckService.initVersionCheck();
  }

  public ngOnDestroy(): void
  {
    if (this.idleServiceTimeoutSubscription !== undefined) { this.idleServiceTimeoutSubscription.unsubscribe(); }
    if (this.authServiceSubscription !== undefined) { this.authServiceSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    if (!this.isLoggedIn)
    {
      this.collapedSideBar = true;
    }
  }

  public ngAfterViewChecked(): void
  {
    // explicit change detection to avoid "expression-has-changed-after-it-was-checked-error"
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Emit event on resize.
   *
   * @param event Resize Event data.
   */
  public onResized(event: ResizedEvent): void
  {
    this.contentResizeEvent.emit(event);
  }

  /**
   * Indicates if the left-nav has been collapsed.
   *
   * @param collapsed Left-nav is collapsed?
   */
  public receiveCollapsed(collapsed: boolean): void
  {
    this.collapedSideBar = collapsed;
  }

  /**
   * Indicates if is logged out.
   *
   * @param logOut is logged out?
   */
  public receiveLogOut(logOut: boolean): void
  {
    this.collapedSideBar = !logOut;
  }

  /**
   * Handle the user confirmation on whatever the user navigate on pending changes.
   *
   * @param action Indicates the user action.
   * If true, it will continue navigation and changes will be lost.
   * If false, the navigation will be stopped to allow saving changes.
   */
  public continueUnsaved(action: boolean): void
  {
    this.unsavedDialogOpened = false;
    this.navigateAwaySelection.next(action);
  }

  /**
   * Provide a public method being used from components to open the unsaved dialog.
   */
  public openUnsavedDialog(): void
  {
    this.unsavedDialogOpened = true;
  }
}
